import {
  RecordLine,
  RecordModel,
  RecordProperties,
  SpecialLines,
} from './record-model';

const logPrefix = '[SAP-LINE-ORDER]';

/**
 * Copies rendered special-line values to the category serialized to SAP and
 * converts an editor row ID anchor to the corresponding SAP LineNum.
 */
export function synchronizeSpecialLinesForSubmission(
  recordModel: RecordModel,
): boolean {
  const documentLines = recordModel?.Categories?.find(
    (category) => category.Id === 'Document_Lines',
  );

  if (
    documentLines == null ||
    documentLines.Lines == null ||
    documentLines.Lines.some((line) => line == null || line.Properties == null)
  ) {
    return fallback(
      'Document_Lines is missing, empty, or contains an invalid line',
    );
  }

  const identityToLine = new Map<number, RecordLine>();
  for (const line of documentLines.Lines) {
    const identity = getDocumentLineIdentity(line);
    if (identity == null || identityToLine.has(identity)) {
      return fallback(
        `Missing or duplicate line identity at array index ${documentLines.Lines.indexOf(
          line,
        )}`,
      );
    }
    identityToLine.set(identity, line);
  }

  const preparedSpecialLines = prepareSpecialLines(
    recordModel,
    documentLines.SpecialLines || [],
    identityToLine,
  );
  if (typeof preparedSpecialLines === 'string') {
    return fallback(preparedSpecialLines);
  }

  const submissionAnchors: number[] = [];
  for (const specialLine of preparedSpecialLines) {
    const submissionAnchor =
      specialLine.originalAnchor === -1
        ? -1
        : getDocumentLineNumber(
            identityToLine.get(specialLine.originalAnchor),
          );
    if (!Number.isInteger(submissionAnchor)) {
      return fallback(
        `Special line anchor ${specialLine.originalAnchor} has no SAP line number`,
      );
    }
    submissionAnchors.push(submissionAnchor);
  }

  preparedSpecialLines.forEach((specialLine, index) => {
    const submissionAnchor = submissionAnchors[index];
    if (specialLine.uiLine != null) {
      setSpecialLineAnchor(specialLine.uiLine, submissionAnchor);
    }
    if (specialLine.serializedLine != null) {
      if (specialLine.uiLine != null) {
        synchronizeSerializedSpecialLine(
          specialLine.serializedLine,
          specialLine.uiLine,
        );
      }
      setSerializedSpecialLineAnchor(
        specialLine.serializedLine,
        submissionAnchor,
      );
    }
  });

  console.info(logPrefix, 'Special lines synchronized for submission', {
    specialLineCount: preparedSpecialLines.length,
  });
  return true;
}

/**
 * Normalizes document lines for a new-version or new-document submission.
 *
 * The caller must pass a submission copy. The live editor model is never
 * modified by this function.
 */
export function normalizeNewVersionDocumentLines(
  recordModel: RecordModel,
): boolean {
  console.info(logPrefix, 'Starting new-version line normalization');

  const documentLines = recordModel?.Categories?.find(
    (category) => category.Id === 'Document_Lines',
  );

  if (
    documentLines == null ||
    documentLines.Lines == null ||
    documentLines.Lines.some((line) => line == null || line.Properties == null)
  ) {
    return fallback(
      'Document_Lines is missing, empty, or contains an invalid line',
    );
  }

  const lines = documentLines.Lines;
  removeTransientDisplaySequence(lines);
  console.info(logPrefix, 'Document lines found', {
    lineCount: lines.length,
    specialLineCount: documentLines.SpecialLines?.length || 0,
  });

  const identityToLine = new Map<number, RecordLine>();
  const groups: RecordLine[][] = [];
  let currentGroup: RecordLine[] = [];

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];

    if (isBomComponent(line)) {
      if (currentGroup.length === 0) {
        return fallback(`Orphan BOM component at array index ${lineIndex}`);
      }
      currentGroup.push(line);
      continue;
    }

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }
    currentGroup = [line];
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  console.info(
    logPrefix,
    'BOM groups identified',
    {
      groupCount: groups.length,
      groupsWithComponents: groups.filter((group) => group.length > 1)
        .length,
      componentCount: groups.reduce(
        (count, group) => count + group.length - 1,
        0,
      ),
      sample: groups.slice(0, 10).map((group) => ({
        parentId: group[0].Id,
        lineCount: group.length,
        componentCount: group.length - 1,
      })),
    },
  );

  for (const line of lines) {
    const identity = getDocumentLineIdentity(line);

    if (identity == null || identityToLine.has(identity)) {
      return fallback(
        `Missing or duplicate line identity at array index ${lines.indexOf(
          line,
        )}`,
      );
    }
    identityToLine.set(identity, line);
  }

  const preparedSpecialLines = prepareSpecialLines(
    recordModel,
    documentLines.SpecialLines || [],
    identityToLine,
  );
  if (typeof preparedSpecialLines === 'string') {
    return fallback(preparedSpecialLines);
  }

  const bomComponentCounts = getExpectedBomComponentCounts(groups);
  if (typeof bomComponentCounts === 'string') {
    return fallback(bomComponentCounts);
  }

  const orderedLines = groups.reduce(
    (result, group) => result.concat(group),
    [] as RecordLine[],
  );
  const finalSpecialAnchorByIdentity = new Map<number, number>();
  let finalLogicalLineIndex = -1;

  for (const group of groups) {
    const actualComponentCount = group.length - 1;
    const expectedComponentCount = getExpectedBomComponentCount(
      group,
      bomComponentCounts.expectedByItemCode,
    );
    const parentLogicalIndex = finalLogicalLineIndex + 1;
    const groupFinalLogicalIndex = parentLogicalIndex + expectedComponentCount;

    group.forEach((line, groupIndex) => {
      const identity = getDocumentLineIdentity(line);
      const logicalLineIndex = parentLogicalIndex + groupIndex;

      // Special lines positioned after a BOM row are visually after the
      // complete group, including components SAP regenerates during Add.
      finalSpecialAnchorByIdentity.set(identity, groupFinalLogicalIndex);
      line.Id = logicalLineIndex;
      setLineNumber(line, logicalLineIndex);
    });

    if (expectedComponentCount > actualComponentCount) {
      bomComponentCounts.inferences.push({
        parentId: group[0].Id,
        itemCode: getItemCode(group[0]),
        actualComponentCount,
        expectedComponentCount,
      });
    }

    finalLogicalLineIndex = groupFinalLogicalIndex;
  }

  for (const specialLine of preparedSpecialLines) {
    const finalAnchor =
      specialLine.originalAnchor === -1
        ? -1
        : finalSpecialAnchorByIdentity.get(specialLine.originalAnchor);

    if (specialLine.uiLine != null) {
      setSpecialLineAnchor(specialLine.uiLine, finalAnchor);
    }

    if (specialLine.serializedLine != null) {
      if (specialLine.uiLine != null) {
        synchronizeSerializedSpecialLine(
          specialLine.serializedLine,
          specialLine.uiLine,
        );
      }
      setSerializedSpecialLineAnchor(specialLine.serializedLine, finalAnchor);
    }
  }

  documentLines.Lines = orderedLines;
  console.info(logPrefix, 'Normalization completed', {
    submittedLineCount: orderedLines.length,
    sapLogicalLineCount: finalLogicalLineIndex + 1,
    inferredBomGroups: bomComponentCounts.inferences,
    finalLineIdentities: orderedLines.map((line) => line.Id),
  });
  return true;
}

/**
 * Returns a safe, non-sensitive snapshot for comparing the UI order with the
 * serialized submission order in browser diagnostics.
 */
export function getDocumentLineOrderDiagnostics(recordModel: RecordModel): any {
  const documentLines = recordModel?.Categories?.find(
    (category) => category.Id === 'Document_Lines',
  );

  const serializedSpecialLines =
    recordModel?.Categories?.find((category) => category.Id === 'SpecialLines')
      ?.Lines || [];

  if (documentLines == null) {
    return {
      lineCount: 0,
      lines: [],
      specialLines: [],
      serializedSpecialLines: getSerializedSpecialLineDiagnostics(
        serializedSpecialLines,
      ),
    };
  }

  return {
    lineCount: documentLines.Lines?.length || 0,
    lines: (documentLines.Lines || []).map((line, index) => ({
      index,
      id: line?.Id,
      lineNum: getPropertyValue(line, ['LineNum', 'LineNumber']),
      itemCode: getPropertyValue(line, ['ItemCode']),
      treeType: getTreeType(line),
    })),
    specialLines: (documentLines.SpecialLines || []).map((line) => ({
      aftLineNum: line?.AftLineNum,
      afterLineNumber: (line as any)?.AfterLineNumber,
      lineSeq: line?.LineSeq,
      lineType: line?.LineType,
      lineText: line?.LineText,
    })),
    serializedSpecialLines: getSerializedSpecialLineDiagnostics(
      serializedSpecialLines,
    ),
  };
}

interface PreparedSpecialLine {
  originalAnchor: number;
  uiLine?: SpecialLines;
  serializedLine?: RecordLine;
}

interface BomComponentCountResult {
  expectedByItemCode: Map<string, number>;
  inferences: Array<{
    parentId: number;
    itemCode: string;
    actualComponentCount: number;
    expectedComponentCount: number;
  }>;
}

function getExpectedBomComponentCounts(
  groups: RecordLine[][],
): BomComponentCountResult | string {
  const expectedByItemCode = new Map<string, number>();

  for (const group of groups) {
    if (!isBomParent(group[0])) {
      continue;
    }

    const itemCode = getItemCode(group[0]);
    if (itemCode === '') {
      return `BOM parent ${group[0].Id} has no item code`;
    }

    const componentCount = group.length - 1;
    const currentExpected = expectedByItemCode.get(itemCode) || 0;
    expectedByItemCode.set(
      itemCode,
      Math.max(currentExpected, componentCount),
    );
  }

  for (const group of groups) {
    if (!isBomParent(group[0]) || group.length > 1) {
      continue;
    }

    const itemCode = getItemCode(group[0]);
    if ((expectedByItemCode.get(itemCode) || 0) === 0) {
      return (
        `BOM parent ${group[0].Id} (${itemCode}) has no component rows, ` +
        'and no complete matching BOM group is available for inference'
      );
    }
  }

  return {
    expectedByItemCode,
    inferences: [],
  };
}

function getExpectedBomComponentCount(
  group: RecordLine[],
  expectedByItemCode: Map<string, number>,
): number {
  const actualComponentCount = group.length - 1;
  if (!isBomParent(group[0])) {
    return actualComponentCount;
  }

  return Math.max(
    actualComponentCount,
    expectedByItemCode.get(getItemCode(group[0])) || 0,
  );
}

/**
 * SAP special lines exist twice in the Angular model: once for rendering on
 * Document_Lines and once in the SpecialLines category that is serialized.
 * The UI copy is authoritative when both are present, but both must be kept in
 * sync so the submitted anchor cannot retain an older line number.
 */
function prepareSpecialLines(
  recordModel: RecordModel,
  uiSpecialLines: SpecialLines[],
  identityToLine: Map<number, RecordLine>,
): PreparedSpecialLine[] | string {
  const serializedCategory = recordModel.Categories.find(
    (category) => category.Id === 'SpecialLines',
  );
  const serializedSpecialLines = serializedCategory?.Lines || [];

  if (
    uiSpecialLines.length > 0 &&
    serializedCategory != null &&
    uiSpecialLines.length !== serializedSpecialLines.length
  ) {
    return (
      'Document_Lines.SpecialLines and the serialized SpecialLines category ' +
      `have different counts (${uiSpecialLines.length} and ` +
      `${serializedSpecialLines.length})`
    );
  }

  const specialLineCount =
    uiSpecialLines.length > 0
      ? uiSpecialLines.length
      : serializedSpecialLines.length;
  const result: PreparedSpecialLine[] = [];

  for (let index = 0; index < specialLineCount; index++) {
    const uiLine = uiSpecialLines[index];
    const serializedLine = serializedSpecialLines[index];

    if (
      serializedLine != null &&
      (serializedLine.Properties == null ||
        getSerializedSpecialLineAnchorProperty(serializedLine) == null)
    ) {
      return `Serialized special line at index ${index} has no anchor field`;
    }

    const anchor =
      uiLine != null
        ? getSpecialLineAnchor(uiLine)
        : getSerializedSpecialLineAnchor(serializedLine);

    if (anchor == null || !Number.isInteger(anchor)) {
      return `Special line at index ${index} has an invalid anchor`;
    }
    if (anchor !== -1 && !identityToLine.has(anchor)) {
      return `Special line anchor ${anchor} cannot be resolved`;
    }

    result.push({
      originalAnchor: anchor,
      uiLine,
      serializedLine,
    });
  }

  console.info(logPrefix, 'Special-line representations validated', {
    uiSpecialLineCount: uiSpecialLines.length,
    serializedSpecialLineCount: serializedSpecialLines.length,
    source:
      uiSpecialLines.length > 0
        ? 'Document_Lines.SpecialLines'
        : 'SpecialLines',
  });

  return result;
}

function removeTransientDisplaySequence(lines: RecordLine[]): void {
  for (const line of lines) {
    if (
      line != null &&
      Object.prototype.hasOwnProperty.call(line, 'BWEBDisplaySequence')
    ) {
      delete (line as any).BWEBDisplaySequence;
    }

    if (line?.Properties == null) {
      continue;
    }

    line.Properties = line.Properties.filter(
      (property) =>
        property.Name !== 'BWEBDisplaySequence' &&
        property.FieldName !== 'BWEBDisplaySequence',
    );
  }
}

function fallback(reason: string): false {
  console.warn(logPrefix, 'Legacy line-order fallback', { reason });
  return false;
}

function getTreeType(line: RecordLine): any {
  const cachedTreeType = line.TreeTypeProp?.ValueAsObject;
  if (cachedTreeType != null && cachedTreeType !== '') {
    return cachedTreeType;
  }

  return line.Properties.find(
    (property) =>
      property.Name === 'TreeType' ||
      property.FieldName === 'TreeType' ||
      (property as any).PropertyName === 'TreeType',
  )?.ValueAsObject;
}

function isBomComponent(line: RecordLine): boolean {
  const treeType = getTreeType(line);
  return treeType === 'I' || treeType === 'iIngredient';
}

function isBomParent(line: RecordLine): boolean {
  const treeType = getTreeType(line);
  return treeType === 'S' || treeType === 'iSalesTree';
}

function getItemCode(line: RecordLine): string {
  const itemCode = getPropertyValue(line, ['ItemCode']);
  return itemCode == null ? '' : itemCode.toString().trim();
}

function getPropertyValue(line: RecordLine, propertyNames: string[]): any {
  return line?.Properties?.find(
    (property) =>
      propertyNames.includes(property.Name) ||
      propertyNames.includes(property.FieldName),
  )?.ValueAsObject;
}

function setLineNumber(line: RecordLine, index: number): void {
  const lineNumber = index.toString();
  line.Properties.forEach((property) => {
    if (
      property.Name === 'LineNum' ||
      property.Name === 'LineNumber' ||
      property.FieldName === 'LineNum' ||
      property.FieldName === 'LineNumber'
    ) {
      property.ValueAsObject = index;
      property.Value = lineNumber;
    }
  });
}

function getDocumentLineIdentity(line: RecordLine): number {
  // Special-line anchors are created from the editor row ID. On a copied
  // document the persisted SAP LineNum can differ from that transient ID,
  // so prefer Id when it is available.
  const recordLineId = Number(line?.Id);
  if (Number.isInteger(recordLineId)) {
    return recordLineId;
  }

  const identityProperty = line.Properties.find((property) => {
    const propertyName = property.Name || property.FieldName;
    if (propertyName !== 'LineNum' && propertyName !== 'LineNumber') {
      return false;
    }
    return property.ValueAsObject !== null && property.ValueAsObject !== '';
  });
  const identity = Number(identityProperty?.ValueAsObject ?? line.Id);
  return Number.isInteger(identity) ? identity : null;
}

function getDocumentLineNumber(line: RecordLine): number {
  const lineNumber = Number(getPropertyValue(line, ['LineNum', 'LineNumber']));
  return Number.isInteger(lineNumber) ? lineNumber : null;
}

function getSpecialLineAnchor(specialLine: any): number {
  const anchor =
    specialLine.AftLineNum !== undefined
      ? specialLine.AftLineNum
      : specialLine.AfterLineNumber;
  return anchor == null || anchor === '' ? null : Number(anchor);
}

function setSpecialLineAnchor(specialLine: any, anchor: number): void {
  if (specialLine.AftLineNum !== undefined) {
    specialLine.AftLineNum = anchor;
  }
  if (specialLine.AfterLineNumber !== undefined) {
    specialLine.AfterLineNumber = anchor;
  }
}

function getSerializedSpecialLineAnchor(line: RecordLine): number {
  const property = getSerializedSpecialLineAnchorProperty(line);
  const value = property?.ValueAsObject ?? property?.Value;
  return value == null || value === '' ? null : Number(value);
}

function getSerializedSpecialLineAnchorProperty(
  line: RecordLine,
): RecordProperties {
  return getSerializedSpecialLineProperty(line, [
    'AftLineNum',
    'AfterLineNumber',
  ]);
}

function setSerializedSpecialLineAnchor(
  line: RecordLine,
  anchor: number,
): void {
  const property = getSerializedSpecialLineAnchorProperty(line);
  property.ValueAsObject = anchor;
  property.Value = anchor.toString();
}

function synchronizeSerializedSpecialLine(
  serializedLine: RecordLine,
  uiLine: SpecialLines,
): void {
  setSerializedSpecialLineProperty(serializedLine, ['LineSeq'], uiLine.LineSeq);
  setSerializedSpecialLineProperty(
    serializedLine,
    ['LineText'],
    uiLine.LineText,
  );
  setSerializedSpecialLineProperty(
    serializedLine,
    ['LineType'],
    uiLine.LineType,
  );
}

function setSerializedSpecialLineProperty(
  line: RecordLine,
  propertyNames: string[],
  value: any,
): void {
  const property = getSerializedSpecialLineProperty(line, propertyNames);
  if (property == null) {
    return;
  }

  property.ValueAsObject = value;
  property.Value = value == null ? '' : value.toString();
}

function getSerializedSpecialLineProperty(
  line: RecordLine,
  propertyNames: string[],
): RecordProperties {
  return line?.Properties?.find((property) => {
    const propertyName =
      property.Name || property.FieldName || (property as any).PropertyName;
    return propertyNames.includes(propertyName);
  });
}

function getSerializedSpecialLineDiagnostics(lines: RecordLine[]): any[] {
  return lines.map((line, index) => ({
    index,
    aftLineNum: getPropertyValue(line, ['AftLineNum', 'AfterLineNumber']),
    lineSeq: getPropertyValue(line, ['LineSeq']),
    lineType: getPropertyValue(line, ['LineType']),
    lineText: getPropertyValue(line, ['LineText']),
  }));
}
